import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../store';
import {
  startAnalysis,
  addTraceEvent,
  completeAnalysis,
  setAnalysisError,
  setCheckpoint,
  clearCheckpoint,
  setIsRunning,
} from '../store/slices/analysisSlice';
import { markAsAnalyzed } from '../store/slices/scenariosSlice';
import { showSnackbar } from '../store/slices/uiSlice';
import { apiService, CheckpointModifications } from '../services/api';
import { SSEEvent, TraceEvent, AllocationResult, CheckpointData } from '../types';


export const useScenarioStream = () => {
  const dispatch = useAppDispatch();
  const { selectedId } = useAppSelector((state) => state.scenarios);
  const { whatifWeights, globalSupplyOverride, globalDemandOverride, parameterOverrides, batchOverrides } = useAppSelector((state) => state.analysis);


  const handleEvent = useCallback((event: SSEEvent, targetId: string, effectiveWeights: any) => {
    switch (event.type) {
      case 'start':
        break;

      case 'trace':
        if (event.event) {
          const traceEvent: TraceEvent = {
            event_type: event.event.event_type || 'progress',
            node: event.event.node || 'system',
            message: event.event.message || '',
            data: event.event.data,
            timestamp: new Date().toISOString(),
          };
          dispatch(addTraceEvent(traceEvent));
        }
        break;

      case 'complete':
        // If status is 'pending', we're at a checkpoint - don't finalize the run
        // This prevents Gate 2 from being skipped when the backend sends both
        // await_confirmation and complete events in sequence
        if (event.status === 'pending') {
          // The run is paused at a gate, don't overwrite checkpoint state
          break;
        }

        const result: AllocationResult = {
          run_id: event.run_id || '',
          scenario_id: targetId,
          status: 'success',
          approval_status: event.approval_status || 'proposed',
          allocation_plan: event.allocation_plan,
          multi_allocation_plan: event.multi_allocation_plan,
          deficit_allocation_plan: event.deficit_allocation_plan,
          mixed_allocation_plan: event.mixed_allocation_plan,
          executive_summary: event.executive_summary || event.allocation_plan?.executive_summary,
          detailed_reasoning: event.detailed_reasoning || event.allocation_plan?.detailed_reasoning,
          risk_factors: event.risk_factors || event.allocation_plan?.risk_factors,
          excluded_region_explanations: event.excluded_region_explanations,
          reconciliation_result: event.reconciliation_result,
          reconciliation_scenario: event.reconciliation_scenario,
          weights: event.weights || effectiveWeights,
          plan_type: event.plan_type,
          comparison_stats: event.comparison_stats,
          business_comparison: event.business_comparison,
          region_contexts: event.region_contexts,
          selected_strategy: event.selected_strategy,
          normalization_note: event.normalization_note,
          comparison_filters: event.comparison_filters,
          chat_brief: event.chat_brief,
          suggested_questions: event.suggested_questions,
        };
        dispatch(completeAnalysis(result));

        if (!effectiveWeights) {
          dispatch(markAsAnalyzed(targetId));
        }

        dispatch(showSnackbar({ message: 'Analysis complete!', severity: 'success' }));
        break;

      case 'error':
        dispatch(setAnalysisError(event.message || 'An error occurred'));
        dispatch(showSnackbar({ message: event.message || 'Analysis failed', severity: 'error' }));
        break;

      case 'await_confirmation':
        // Graph paused at checkpoint - show checkpoint UI
        // Note: Backend sends checkpoint_data for Gate 1, but 'data' for Gate 2
        const checkpointData = event.checkpoint_data || event.data;
        if (checkpointData) {
          dispatch(setCheckpoint(checkpointData as CheckpointData));
          dispatch(showSnackbar({
            message: 'Review checkpoint before optimization',
            severity: 'info'
          }));
        }
        break;

      case 'resume':
        // Graph resuming from checkpoint - restart agent indicators
        dispatch(setIsRunning(true));
        dispatch(showSnackbar({
          message: 'Resuming optimization with your modifications...',
          severity: 'info'
        }));
        break;
    }
  }, [dispatch]);

  const runAnalysis = useCallback(
    (scenarioId?: string, weights?: Record<string, number> | null, supply?: number | null, demand?: number | null, skipStateSetup?: boolean, params?: Record<string, number> | null, regionalOverrides?: Record<string, any> | null, strategyId?: string | null, batchScopedOverrides?: Record<string, Record<string, number>> | null, comparisonFilters?: Record<string, any> | null) => {
      const targetId = scenarioId || selectedId;
      if (!targetId) {
        dispatch(showSnackbar({ message: 'No scenario selected', severity: 'warning' }));
        return null;
      }

      const effectiveWeights = weights !== undefined ? weights : whatifWeights;
      const effectiveSupply = supply !== undefined ? supply : globalSupplyOverride;
      const effectiveDemand = demand !== undefined ? demand : globalDemandOverride;
      const effectiveParams = params !== undefined ? params : parameterOverrides;
      const effectiveBatchOverrides = batchScopedOverrides !== undefined ? batchScopedOverrides : batchOverrides;

      // Only dispatch startAnalysis if not already set up (prevents double dispatch)
      if (!skipStateSetup) {
        dispatch(startAnalysis({
          scenarioId: targetId,
          weights: effectiveWeights || undefined,
          globalSupplyOverride: effectiveSupply || undefined,
          globalDemandOverride: effectiveDemand || undefined,
          parameterOverrides: effectiveParams || undefined,
          batchOverrides: effectiveBatchOverrides || undefined
        }));
      }

      const cleanup = apiService.streamScenarioRun(
        targetId,
        effectiveWeights,
        effectiveSupply,
        effectiveDemand,
        (event: SSEEvent) => handleEvent(event, targetId, effectiveWeights),
        (error: Error) => {
          dispatch(setAnalysisError(error.message));
          dispatch(showSnackbar({ message: error.message, severity: 'error' }));
        },
        () => {
        },
        effectiveParams,
        regionalOverrides,
        strategyId,
        effectiveBatchOverrides,
        comparisonFilters
      );

      return cleanup;
    },
    [dispatch, selectedId, whatifWeights, parameterOverrides, batchOverrides, handleEvent]
  );

  const resumeAnalysis = useCallback(
    (scenarioId: string, modifications: CheckpointModifications) => {
      // Keep isRunning=true but keep existing traces
      // AnalysisSlice.setCheckpoint already sets isRunning=false, so we might need to reset it
      // but the startAnalysis action resets everything. We want to KEEP what we have.

      // Dispatch a small action to indicate we are resuming (restart indicators)
      dispatch(clearCheckpoint());
      // We don't want to clear traces, so we don't call startAnalysis
      // but we need isRunning = true
      dispatch(setIsRunning(true));

      const cleanup = apiService.streamConfirmCheckpoint(
        scenarioId,
        modifications,
        (event: SSEEvent) => handleEvent(event, scenarioId, modifications.modified_weights),
        (error: Error) => {
          dispatch(setAnalysisError(error.message));
          dispatch(showSnackbar({ message: error.message, severity: 'error' }));
        },
        () => { }
      );

      return cleanup;
    },
    [dispatch, handleEvent]
  );

  return { runAnalysis, resumeAnalysis };
};

export { useAppDispatch, useAppSelector } from '../store';
